import { useState } from 'react'
import { KpiMetricCard } from '@/components/shared/KpiMetricCard'
import { RegulatoryFiltersBar } from '@/components/regulatory/RegulatoryFiltersBar'
import { RegulatoryKpiRail } from '@/components/regulatory/RegulatoryKpiRail'
import { RegulatorVelocityChart } from '@/components/regulatory/RegulatorVelocityChart'
import { FrameworkCoverageGrid } from '@/components/regulatory/FrameworkCoverageGrid'
import { FineTrendSmallMultiples } from '@/components/regulatory/FineTrendSmallMultiples'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import {
  useRegulatoryFrameworks,
  useRegulatoryKpis,
  type RegulatoryFilters,
} from '@/hooks/useRegulatoryQueries'

const DEFAULT_FILTERS: RegulatoryFilters = {
  jurisdiction: 'all',
  framework: 'all',
  timeframe: '90d',
}

/**
 * `/regulatory` — wave-1 "regulatory radar" landing surface.
 *
 * Stage 1 shipped: page shell + filter bar + KPI rail + P4 Regulator Velocity.
 * Stage 2 adds: `frameworks` migration + P3 Framework Coverage grid +
 *               4th KPI card ("Frameworks tracked") + populated Framework dropdown.
 * Stage 3 adds: P7 Fine Trend by Framework (GDPR + SEC Cyber only — server
 *               returns only full/partial coverage frameworks).
 *
 * Wave-1 P5 adoption: page-level filter state seeds from saved prefs via
 * `useFilterDefaultsFromPrefs<RegulatoryFilters>('regulatory')` so users
 * who saved a default jurisdiction/timeframe land on it directly. Captain
 * Input #4: existing users with no prefs continue to see the global view
 * (no synthesised migration). Locked by `filter-rail-must-adopt-prefs`
 * ESLint rule (allowlisted page).
 *
 * Wave-1 P4f (audit item 22): KPI rail + framework grid filters now
 * honour the active filter state; tile labels are derived from the
 * resolved timeframe rather than hardcoded "(90D)".
 */
export function RegulatoryPage() {
  const { defaults: prefDefaults } =
    useFilterDefaultsFromPrefs<RegulatoryFilters>('regulatory')
  const [filters, setFilters] = useState<RegulatoryFilters>(
    prefDefaults ?? DEFAULT_FILTERS,
  )

  const fetchFilters = {
    jurisdiction: filters.jurisdiction,
    timeframe: filters.timeframe,
  }

  const { data: frameworksData } = useRegulatoryFrameworks(fetchFilters)
  const { data: kpis } = useRegulatoryKpis(fetchFilters)

  const frameworkOptions =
    frameworksData?.frameworks.map((f) => ({ slug: f.slug, name: f.name })) ??
    []

  const frameworksTracked = kpis?.frameworksTracked ?? 0

  return (
    <main
      className="flex flex-col gap-6 p-6"
      aria-label="Regulatory radar"
      data-testid="regulatory-page"
    >
      <header>
        <h1 className="text-2xl font-semibold">Regulatory</h1>
        <p className="text-muted-foreground">
          Regulatory landscape, velocity, and framework coverage.
        </p>
      </header>

      <RegulatoryFiltersBar
        filters={filters}
        onChange={setFilters}
        frameworkOptions={frameworkOptions}
      />

      <RegulatoryKpiRail
        filters={fetchFilters}
        extraCardsSlot={
          <KpiMetricCard
            label="Frameworks tracked"
            value={frameworksTracked}
          />
        }
      />

      <RegulatorVelocityChart
        filters={{
          jurisdiction: filters.jurisdiction,
          timeframe: filters.timeframe,
        }}
      />

      <FrameworkCoverageGrid filters={fetchFilters} />

      <FineTrendSmallMultiples
        filters={{
          jurisdiction: filters.jurisdiction,
          timeframe: filters.timeframe,
          framework: filters.framework,
        }}
      />

      <footer className="text-xs italic text-muted-foreground">
        Rule horizon scanner and deadline calendar coming next.
      </footer>
    </main>
  )
}
